// Split-sleeper-berth helpers used across BOTH tiers — symmetric (Canada, US
// Passenger) and asymmetric (US Property). This file is kept separate from
// index.ts only to preserve the package's one-way dependency graph: the tier
// files (symmetric.ts/asymmetric.ts) import these primitives, while index.ts
// imports the tier evaluators. Folding these into index.ts would create an
// index <-> tier import cycle.
import { getSplitPeriodDuration, type IState, type TWorkdayResetInfo } from "../helpers";
import { startNewWorkday, getConfigWorkdayMaximum } from "../hosCalculator";
import { clearEmergencyResponseSuppressedViolation } from "../exemptions/emergencyResponse";
import { isSplitCompleteBlockedByViolation } from "./blockingViolations";

// True while a sleeper-berth split is partially formed: at least one period is
// tracked (accumulating or banked) and the pairing has not completed (!completedSplitSB).
export const hasPartialSplitForCurrentWorkdaySymmetric = (state: IState): boolean => {
    if (!state.workday.completedSplitSB && state.workday.splitSB && state.workday.splitSB.periods.length > 0) {
        return true;
    }
    return false;
};

export const unfreezePriorSplitPeriods = (state: IState) => {
    let workdayMaximum = getConfigWorkdayMaximum(state);
    if (!workdayMaximum || !state.workday.start) {
        return;
    }
    // Clear any stored split periods which are no longer valid
    state.workday.splitSB = {
        inSleeper: false,
        periods: []
    };
    // Reset workday end time, removing the freezed SB periods
    let workdayExtended = state.workday.workdayExtended || 0;
    state.workday.end?.setTime(state.workday.start.getTime() + workdayMaximum + workdayExtended);
};

export const createSplitSB = (state: IState) => {
    // Need to create propertySplitSB for noSplit because split was allowed for log before sep 29th 2020
    if (state.config.splitConfig?.type !== "symmetric") {
        state.workday.propertySplitSB = {
            inOffDuty: false,
            inSleeper: false,
            sbPeriods: [],
            offDutyPeriods: []
        };
    } else {
        state.workday.splitSB = {
            inSleeper: false,
            periods: []
        };
    }
};

export const removeCurrentWorkdayViolation = (state: IState, fromDate: Date) => {
    // Remove violation that is in range of the Log that we are reprocessing, both completed and ongoing violation
    let reCalculationNeeded = true;
    if (state.currentViolations.Workday) {
        if (state.currentViolations.Workday.fromDate.getTime() >= fromDate.getTime()) {
            delete state.currentViolations.Workday;
            // The recalc below rebuilds this violation from scratch, so the Emergency Response
            // marker has to go with it — otherwise an exempt driver stays blocked here where a
            // non-exempt one would not (MYG-139401).
            clearEmergencyResponseSuppressedViolation(state, "Workday");
        } else {
            // currentViolations starts before any log we are going to reprocess, no recalculation Needed
            reCalculationNeeded = false;
        }
    }
    return reCalculationNeeded;
};

// Re-exported for split-sleeper callers that already consume this shared module. The implementation
// lives in blockingViolations.ts so next-availability code can reuse it without importing through
// this module's hosCalculator dependency.
export { isSplitCompleteBlockedByViolation };
export { SPLIT_BLOCKING_VIOLATIONS } from "./blockingViolations";

// Append the current log to the workday's split-log buffer, but only if it is newer than
// the last log already recorded. Shared by both tiers' recordSplitLogs* — the append step
// is identical; only the per-tier "is there a partial split in progress?" guard differs.
export const appendCurrentSplitLog = (state: IState) => {
    if (!state.workday.splitLogs) {
        state.workday.splitLogs = [];
    }
    const splitLogs = state.workday.splitLogs;
    const dateLimit = splitLogs.length > 0 ? splitLogs[splitLogs.length - 1].dateTime : undefined;
    if (!dateLimit || state.currentDate > dateLimit) {
        splitLogs.push(state.current);
    }
};

// Shared preamble for both tiers' recalculateWorkdayViolationsForCurrentWorkday*. Validates
// that the workday is in a recalculable state and — matching the originals — clears any
// existing Workday violation as a side effect, then returns the values both loops seed from:
// the starting workdayEnd, the workday maximum, the (now-narrowed) split-log buffer, and the
// workday config. Returns undefined to bail. The two per-tier signals — whether the tier's
// split-period tracker holds anything, and whether its splitConfig is present — are passed in
// because they differ by geometry; everything else is identical across tiers.
//
// removeCurrentWorkdayViolation mutates state, so hasSplitPeriods/hasSplitConfig must gate it
// exactly as the inline guards did — hence both are checked before it runs.
export const beginWorkdayRecalc = (state: IState, hasSplitPeriods: boolean, hasSplitConfig: boolean) => {
    const workdayMaximum = getConfigWorkdayMaximum(state);
    const workdayConfig = state.config.workday;
    if (
        !hasSplitPeriods ||
        !state.workday ||
        !state.workday.start ||
        !workdayConfig ||
        !workdayMaximum ||
        !state.workday.splitLogs ||
        !state.workday.splitLogs.length ||
        !hasSplitConfig
    ) {
        return undefined;
    }
    // If it exists, remove the existing workday violation and check if we need to recalculate.
    if (!removeCurrentWorkdayViolation(state, state.workday.splitLogs[0].dateTime)) {
        return undefined;
    }
    const workdayEnd = state.workday.start.getTime() + workdayMaximum + (state.workday.workdayExtended ?? 0);
    return { workdayEnd, workdayMaximum, splitLogs: state.workday.splitLogs, workdayConfig };
};

export const completeSplit = (state: IState, workdayResetInfo?: TWorkdayResetInfo) => {
    let splitLogs = state.workday.splitLogs || [];
    let dateLimit = splitLogs.length > 0 ? splitLogs[splitLogs.length - 1].dateTime : undefined;
    state.logs = splitLogs.concat(state.logs.filter(l => !dateLimit || l.dateTime > dateLimit));
    state.i = 0;
    state.allocatedCountIndex = splitLogs.length;
    state.next = state.logs[0];
    state.workday.completedSplitSB = true;

    // Don't want to recount consecutive cycle off time after a split sb
    state.cycle.pauseCountingCycleOff = true;
    startNewWorkday(state, state.next.dateTime, false, state.workday.isAdverseLogApplied, workdayResetInfo);
    state.workday.earnedSplitExclusion = durationOfCompletedPairingLaterLeg(workdayResetInfo);
};

// The later leg of the completed pairing is excluded from the new workday's window — that
// exclusion is EARNED (locked in by the completion), unlike a speculative freeze. MYG-133117.
const durationOfCompletedPairingLaterLeg = (workdayResetInfo?: TWorkdayResetInfo): number | undefined => {
    if (workdayResetInfo === undefined || !("periods" in workdayResetInfo)) {
        return undefined;
    }
    const laterLeg = workdayResetInfo.periods[workdayResetInfo.periods.length - 1];
    if (laterLeg === undefined) {
        return undefined;
    }
    return laterLeg.toDate.getTime() - laterLeg.fromDate.getTime();
};

export const getRemainingSplitDuration = (state: IState) => {
    let offDutyMin = state.config.workday?.offDutyMinimum;
    const splitGeometry = state.config.splitConfig;
    // INTENTIONAL parity change (MYG-77582, context §7.4): this gates only on
    // enablePreventSplitCompleteWhenViolationFix. `main` also OR'd the retired
    // `isMismappedCanadianRuleset` special-case; dropping it is deliberate — mismapped Canadian
    // rulesets (Alberta / North of 60) are genuinely Canadian now, not a regression. (Reachable, not U-gated.)
    if (
        !state.customCodes.enablePreventSplitCompleteWhenViolationFix ||
        !splitGeometry ||
        !offDutyMin ||
        !state.current ||
        ["OFF", "SB", "PC"].indexOf(state.current.status) === -1
    ) {
        return undefined;
    }
    let remainingSplitDuration = offDutyMin;

    if (splitGeometry.type === "symmetric") {
        let splitInfo = state.workday.splitSB;
        if (!splitInfo || !splitInfo.inSleeper) {
            return undefined;
        }
        if (!isSplitCompleteBlockedByViolation(state)) {
            // subtract all eligible sb breaks except the last ongoing from offDutyMin to get the remaining rest duration
            remainingSplitDuration = splitInfo.periods
                .slice(0, -1)
                .map(p => getSplitPeriodDuration(p))
                .reduce((acc, cur) => acc - cur, offDutyMin);
        }
    } else if (splitGeometry.type === "asymmetric") {
        let splitInfo = state.workday.propertySplitSB;
        let splitConfig = state.propertySplitSubConfig;
        if (splitInfo && splitConfig) {
            let offDutyPeriods = splitInfo.offDutyPeriods;
            if (!isSplitCompleteBlockedByViolation(state)) {
                remainingSplitDuration = offDutyPeriods.slice(0, -1).reduce((acc, cur) => acc - getSplitPeriodDuration(cur), offDutyMin);
            }
        }
    }
    return Math.max(0, remainingSplitDuration);
};
